import { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Image, Rect, Transformer } from 'react-konva';
import { Box, Button, Stack } from '@mui/joy';

const ImageEditor = ({ src, setTestingPage }) => {
    const [image, setImage] = useState(null);

    useEffect(() => {
        const handleImage = () => {
            if (src) {
                const img = new window.Image();
                img.src = src;
                img.onload = () => {
                    setImage(img);
                    setRectangle(null);
                };
            }
        };

        handleImage();
    }, [src]);


    const [rectangle, setRectangle] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [newRectangle, setNewRectangle] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const stageRef = useRef(null);

    // Start drawing
    const handleMouseDown = (e) => {
        if (!image || rectangle) return; // Prevent drawing if we already have a rectangle
        const pos = e.target.getStage().getPointerPosition();
        setIsDrawing(true);
        setNewRectangle({
            x: pos.x,
            y: pos.y,
            width: 0,
            height: 0,
            id: Date.now().toString()
        });
    };

    // Draw while moving mouse
    const handleMouseMove = (e) => {
        if (!isDrawing) return;
        const pos = e.target.getStage().getPointerPosition();
        setNewRectangle({
            ...newRectangle,
            width: pos.x - newRectangle.x,
            height: pos.y - newRectangle.y
        });
    };

    // Finish drawing
    const handleMouseUp = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        // Only add rectangle if it has some size
        if (Math.abs(newRectangle.width) > 5 && Math.abs(newRectangle.height) > 5) {
            // Ensure positive width and height
            const rect = {
                ...newRectangle,
                width: Math.abs(newRectangle.width),
                x: newRectangle.width < 0 ? newRectangle.x + newRectangle.width : newRectangle.x,
                height: Math.abs(newRectangle.height),
                y: newRectangle.height < 0 ? newRectangle.y + newRectangle.height : newRectangle.y
            };
            setRectangle(rect);
        }
        setNewRectangle({ x: 0, y: 0, width: 0, height: 0 });
    };

    // Reset rectangle to allow drawing a new one
    const handleReset = () => {
        setRectangle(null);
    };

    const handleReplaceTestInput = async () => {
        // This is the key change - we need to explicitly export the entire stage
        const dataURL = stageRef.current.toDataURL({
            pixelRatio: 2, // Higher quality
            mimeType: 'image/png'
        });
        
        // Convert the data URL to a blob using async/await
        const response = await fetch(dataURL);
        const blob = await response.blob();
        
        // Create file and object URL
        const file = new File([blob], 'highlighted-image.png', { type: 'image/png' });
        const contents = URL.createObjectURL(file);
        
        // Update state with the new image
        setTestingPage({
            name: file.name,
            file: file,
            url: contents,
        });
        setRectangle(null);
    };


    // Update stage size and recalculate scale when container or image changes
    const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
    const containerRef = useRef(null);

    useEffect(() => {
        const checkSize = () => {
            if (containerRef.current) {
                const WIDTH_SCALE = .96;
                const width = containerRef.current.offsetWidth * WIDTH_SCALE;
                const height = containerRef.current.offsetHeight;
                setStageSize({ width, height });
            }
        };
        checkSize();

        // window.addEventListener('resize', checkSize);
        // return () => window.removeEventListener('resize', checkSize);
    }, []);

    useEffect(() => {
        const calculateScale = () => {
            if (!containerRef.current || !image) return;

            const scale = stageSize.width / image.width;
            setStageSize(previousStageSize => ({ width: previousStageSize.width, height: image.height * scale }));
        };

        calculateScale();

    }, [image]);


    return (
        <>
            {rectangle && (
                <Stack spacing={1} direction="row">
                    <Button size='sm' onClick={handleReset}>Remove Rectangle</Button>
                    <Button size='sm' color='success' onClick={handleReplaceTestInput}>Save</Button>
                </Stack>
            )}
            <Box
                sx={{
                    width: '100%',
                    height: 384,
                    overflow: 'auto',
                }}
                ref={containerRef}
            >
                <Stage
                    width={stageSize.width}
                    height={stageSize.height}
                    ref={stageRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                >
                    <Layer>
                        {image && (
                            <Image
                                image={image}
                                width={stageSize.width}
                                height={stageSize.height}
                            />
                        )}
                        {rectangle && (
                            <Rect
                                key={rectangle.id}
                                x={rectangle.x}
                                y={rectangle.y}
                                width={rectangle.width}
                                height={rectangle.height}
                                fill="transparent"
                                stroke="red"
                                strokeWidth={1}
                            />
                        )}
                        {isDrawing && (
                            <Rect
                                x={newRectangle.x}
                                y={newRectangle.y}
                                width={newRectangle.width}
                                height={newRectangle.height}
                                fill="transparent"
                                stroke="red"
                                strokeWidth={1}
                            />
                        )}
                    </Layer>
                </Stage>
            </Box>
        </>
    );
};

export default ImageEditor;